import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import AnimatedCounter from '../components/AnimatedCounter';
import RegisterDialog from '../components/RegisterDialog';
import { useSiteContent } from '../context/SiteContentContext';
import { homeIcons, media, creditRedeem, events as staticEvents } from '../siteContent';
import CmsImage from '../components/CmsImage';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

const Home = () => {
  const { home: c } = useSiteContent();
  const [eventList, setEventList] = useState(
    staticEvents.map((e) => ({ id: e.title, date: e.date, title: e.title, tag: e.tag, format: e.format }))
  );

  useEffect(() => {
    axios.get(`${API}/events/`).then((r) => {
      if (r.data && r.data.length) {
        setEventList(r.data.map((e) => ({ id: e.id, date: e.event_date, title: e.title, tag: e.category, format: e.format })));
      }
    }).catch(() => {});
  }, []);

  return (
    <div className="bg-[#FDFBF9] overflow-hidden">
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 50% at 80% 10%, rgba(231,76,60,0.08) 0%, rgba(253,251,249,0) 70%)' }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-6 animate-fadeInUp">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EAE5E0] px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#003366] mb-6" data-testid="hero-badge">
              <span className="w-2 h-2 rounded-full bg-[#7CA982]" /> {c.hero.badge}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.7rem] leading-[1.05] tracking-tight font-extrabold text-[#1A1A1A]">
              {c.hero.headlinePre}<span className="text-[#E74C3C]">{c.hero.headlineHighlight}</span>
            </h1>
            <p className="mt-6 text-lg text-[#666666] leading-relaxed max-w-xl">{c.hero.subtext}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/membership" data-testid="hero-join-btn">
                <Button className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-8 py-6 text-base font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_35px_rgba(231,76,60,0.35)] group">
                  {c.hero.ctaPrimary}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/membership" data-testid="hero-volunteer-btn">
                <Button variant="outline" className="rounded-full border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white px-8 py-6 text-base font-medium transition-all">
                  {c.hero.ctaSecondary}
                </Button>
              </Link>
            </div>
            <Link to="/how-we-help" className="inline-flex items-center gap-1.5 mt-6 text-sm font-medium text-[#003366] hover:text-[#E74C3C] transition-colors" data-testid="hero-explore-link">
              {c.hero.exploreLabel} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6 animate-fadeInUp">
            <div className="relative">
              <CmsImage src={c.hero.image} alt="Professionals collaborating" className="rounded-[2rem] shadow-2xl w-full h-[420px] lg:h-[520px] object-cover" loading="eager" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-[#EAE5E0] hidden sm:block">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-3">
                    {media.avatars.map((a, i) => (
                      <img key={i} src={a} alt="member" className="w-10 h-10 rounded-full border-2 border-white object-cover" loading="lazy" decoding="async" />
                    ))}
                  </div>
                  <div>
                    <p className="font-heading font-bold text-[#003366] text-lg leading-none">{c.hero.statNumber}</p>
                    <p className="text-xs text-[#666666] mt-1">{c.hero.statLabel}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-[#EAE5E0] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-wrap justify-center lg:justify-between gap-x-8 gap-y-4">
          {c.trustBar.map((label, i) => (
            <div key={label} className="flex items-center gap-2.5">
              <Icon name={homeIcons.trustBar[i] || 'Check'} className="w-5 h-5 text-[#E74C3C]" />
              <span className="text-sm font-medium text-[#1A1A1A]">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E74C3C] mb-4">{c.whoWeAre.eyebrow}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A]">
            {c.whoWeAre.headingLine1} <br className="hidden sm:block" /><span className="text-[#003366]">{c.whoWeAre.headingHighlight}</span>
          </h2>
          <p className="mt-8 text-lg text-[#666666] leading-relaxed">{c.whoWeAre.para1}</p>
          <p className="mt-5 text-lg text-[#666666] leading-relaxed">{c.whoWeAre.para2}</p>
        </div>

        <div className="max-w-6xl mx-auto px-6 mt-16">
          <div className="flex flex-wrap items-center justify-center gap-3 lg:gap-2">
            {c.ecosystem.map((label, i) => (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center gap-3 bg-white rounded-2xl border border-[#EAE5E0] px-6 py-5 min-w-[120px] hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="w-12 h-12 rounded-full bg-[#003366]/5 flex items-center justify-center">
                    <Icon name={homeIcons.ecosystem[i] || 'Circle'} className="w-6 h-6 text-[#003366]" />
                  </div>
                  <span className="font-heading font-semibold text-[#1A1A1A]">{label}</span>
                </div>
                {i < c.ecosystem.length - 1 && <ArrowRight className="w-5 h-5 text-[#E74C3C] rotate-90 lg:rotate-0" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE SUPPORT YOU */}
      <section id="support" className="py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E74C3C] mb-4">{c.support.eyebrow}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A]">{c.support.heading}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.support.pillars.map((p, i) => {
              const meta = homeIcons.pillars[i] || { icon: 'Circle', accent: '#E74C3C', featured: false };
              return (
                <div key={p.title} className={`rounded-3xl border border-[#EAE5E0] bg-[#FDFBF9] p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${meta.featured ? 'lg:row-span-2' : ''}`}>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: `${meta.accent}15` }}>
                    <Icon name={meta.icon} className="w-7 h-7" style={{ color: meta.accent }} />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">{p.title}</h3>
                  <ul className="space-y-2.5">
                    {(p.points || []).map((pt) => (
                      <li key={pt} className="flex items-start gap-2.5 text-[#666666]">
                        <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: meta.accent }} />
                        <span className="text-[15px]">{pt}</span>
                      </li>
                    ))}
                  </ul>
                  {p.note && <p className="mt-5 text-xs text-[#666666] italic border-l-2 border-[#E74C3C] pl-3">{p.note}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR DIFFERENCE */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E74C3C] mb-4">{c.difference.eyebrow}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A]">{c.difference.heading}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.difference.items.map((title, i) => (
              <div key={title} className="bg-white rounded-2xl border border-[#EAE5E0] p-6 hover:border-[#E74C3C] transition-colors duration-300">
                <div className="w-11 h-11 rounded-xl bg-[#E74C3C]/10 flex items-center justify-center mb-4">
                  <Icon name={homeIcons.differences[i] || 'Star'} className="w-5 h-5 text-[#E74C3C]" />
                </div>
                <h4 className="font-heading font-semibold text-[#1A1A1A] leading-snug">{title}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE — WILL */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="rounded-[2.5rem] bg-[#003366] text-white p-10 lg:p-16 grid lg:grid-cols-2 gap-12 items-center relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#E74C3C]/20 blur-3xl" />
            <div className="relative">
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E74C3C] mb-4">{c.will.eyebrow}</p>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold leading-tight">{c.will.heading}</h2>
              <p className="mt-6 text-white/80 leading-relaxed">{c.will.para}</p>
              <p className="mt-5 text-sm text-white/60">{c.will.note}</p>
              <Link to="/corporate-life-series" data-testid="will-cta-btn">
                <Button className="mt-8 rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-8 py-6 text-base group">
                  {c.will.cta}<ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            <div className="relative grid grid-cols-1 gap-3">
              {c.will.points.map((w) => (
                <div key={w} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4 border border-white/10">
                  <Check className="w-5 h-5 text-[#7CA982]" /><span className="font-medium">{w}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMMUNITY MODEL */}
      <section id="community" className="py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E74C3C] mb-4">{c.community.eyebrow}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A]">{c.community.heading}</h2>
            <p className="mt-6 text-lg text-[#666666] leading-relaxed">{c.community.para}</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
            {c.community.flow.map((label, i) => (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center gap-3 bg-[#FDFBF9] rounded-2xl border border-[#EAE5E0] px-6 py-5 min-w-[130px]">
                  <div className="w-12 h-12 rounded-full bg-[#E74C3C]/10 flex items-center justify-center">
                    <Icon name={homeIcons.creditFlow[i] || 'Circle'} className="w-6 h-6 text-[#E74C3C]" />
                  </div>
                  <span className="font-heading font-semibold text-[#1A1A1A] text-sm">{label}</span>
                </div>
                {i < c.community.flow.length - 1 && <ArrowRight className="w-5 h-5 text-[#003366] rotate-90 sm:rotate-0" />}
              </React.Fragment>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {(c.community.redeem || creditRedeem).map((r) => (
              <span key={r} className="rounded-full bg-[#003366]/5 border border-[#003366]/10 text-[#003366] px-5 py-2 text-sm font-medium">{r}</span>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section id="events" className="py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E74C3C] mb-4">{c.events.eyebrow}</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A]">{c.events.heading}</h2>
            </div>
            <Link to="/events">
              <Button variant="outline" className="rounded-full border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white">See All Events</Button>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eventList.map((e) => {
              const [day, month] = e.date.split(' ');
              return (
                <div key={e.id} className="bg-white rounded-3xl border border-[#EAE5E0] p-6 flex flex-col gap-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid="event-card">
                  <div className="flex gap-5">
                    <div className="flex flex-col items-center justify-center rounded-2xl bg-[#003366] text-white w-16 h-16 flex-shrink-0">
                      <span className="font-heading font-bold text-xl leading-none">{day}</span>
                      <span className="text-xs uppercase mt-1">{month}</span>
                    </div>
                    <div className="flex-1">
                      <span className="inline-block text-xs font-semibold uppercase tracking-wide text-[#E74C3C] mb-1">{e.tag} · {e.format}</span>
                      <h3 className="font-heading font-semibold text-[#1A1A1A] leading-snug">{e.title}</h3>
                    </div>
                  </div>
                  <RegisterDialog kind="event" refId={e.id} title={e.title} label="Register — Free" testid="event-register-btn"
                    triggerClassName="w-full rounded-full bg-[#E74C3C]/10 text-[#E74C3C] hover:bg-[#E74C3C] hover:text-white font-medium transition-colors" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="py-24 bg-[#003366] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E74C3C] mb-4">{c.impact.eyebrow}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{c.impact.heading}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-12">
            {c.impact.stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-heading font-extrabold text-4xl lg:text-6xl text-white">
                  <AnimatedCounter value={Number(s.value) || 0} suffix={s.suffix} />
                </div>
                <p className="mt-3 text-white/70 text-sm lg:text-base">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E74C3C] mb-4">{c.testimonials.eyebrow}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A]">{c.testimonials.heading}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.testimonials.items.map((t, i) => (
              <div key={t.name} className="rounded-3xl border border-[#EAE5E0] bg-[#FDFBF9] p-8 hover:shadow-lg transition-all duration-300 text-center">
                <p className="font-heading text-xl font-semibold text-[#003366] leading-snug">"{t.quote}"</p>
                <div className="mt-6">
                  <p className="font-semibold text-[#1A1A1A] text-sm">{t.name}</p>
                  <p className="text-xs text-[#666666]">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-[2.5rem] bg-gradient-to-br from-[#E74C3C] to-[#C0392B] text-white text-center px-8 py-16 lg:py-20 relative overflow-hidden">
            <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight relative">
              {c.finalCta.headingLine1}<br />{c.finalCta.headingLine2}
            </h2>
            <p className="mt-6 text-white/90 text-lg relative">{c.finalCta.subtext}</p>
            <Link to="/membership" data-testid="final-cta-btn" className="relative">
              <Button className="mt-8 rounded-full bg-white text-[#E74C3C] hover:bg-[#FDFBF9] px-10 py-6 text-base font-semibold group">
                {c.finalCta.cta}<ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
